import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Button,
  Tooltip,
  Grid
} from '@mui/material';
import {
  ArrowBack,
  MoveDown,
  MoveUp,
  Person,
  CalendarToday,
  Note,
  Receipt,
  Storage,
  Business,
  Inventory,
  SwapHoriz,
  LocalShipping,
  CompareArrows
} from '@mui/icons-material';
import { DataService } from '../services/data-service';
import { Operation, OperationDetailResponse, Transfer, TransferType, TransferDirection } from '../types/Operation.types';
import { format } from 'date-fns';

interface OperationDetailsProps {
  operationId: string;
  onBack: () => void;
}

const getTransferTypeInfo = (type: TransferType) => {
  switch (type) {
    case TransferType.MoveIn:
      return { label: 'Move In', color: 'success' as const, icon: <MoveDown /> };
    case TransferType.MoveOut:
      return { label: 'Move Out', color: 'warning' as const, icon: <MoveUp /> };
    case TransferType.Reallocation:
      return { label: 'Reallocation', color: 'info' as const, icon: <SwapHoriz /> };
    case TransferType.Shipment:
      return { label: 'Shipment', color: 'primary' as const, icon: <LocalShipping /> };
    case TransferType.Transfer:
      return { label: 'Transfer', color: 'secondary' as const, icon: <CompareArrows /> };
    default:
      return { label: 'Unknown', color: 'default' as const, icon: <SwapHoriz /> };
  }
};

const getDirectionInfo = (direction: TransferDirection) => {
  switch (direction) {
    case TransferDirection.In:
      return { label: 'In', color: 'success' as const, icon: <MoveDown /> };
    case TransferDirection.Out:
      return { label: 'Out', color: 'warning' as const, icon: <MoveUp /> };
    default:
      return { label: 'Unknown', color: 'default' as const, icon: <SwapHoriz /> };
  }
};

const OperationDetails: React.FC<OperationDetailsProps> = ({ operationId, onBack }) => {
  const [operation, setOperation] = useState<Operation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOperationDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await DataService.get(`api/Operation/${operationId}`);

        if (!response.ok) {
          throw new Error('Failed to fetch operation details');
        }

        const data: OperationDetailResponse = await response.json();
        setOperation(data);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch operation details');
        setOperation(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOperationDetails();
  }, [operationId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !operation) {
    return (
      <Box p={2}>
        <Alert severity="error" action={
          <Button onClick={onBack}>
            Go Back
          </Button>
        }>
          {error || 'Operation not found'}
        </Alert>
      </Box>
    );
  }

  const transferTypeInfo = getTransferTypeInfo(operation.type);
  const transfersIn = operation.transfers.filter(t => t.direction === TransferDirection.In);
  const transfersOut = operation.transfers.filter(t => t.direction === TransferDirection.Out);

  return (
    <Box>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={onBack} aria-label="ArrowBack">
                <ArrowBack />
              </IconButton>
              {transferTypeInfo.icon}
              <Typography variant="h5" fontWeight="bold">
                {transferTypeInfo.label} Operation Details
              </Typography>
              <Chip
                label={transferTypeInfo.label}
                size="small"
                color={transferTypeInfo.color}
                variant="filled"
              />
            </Stack>
          </Stack>

          <Grid container spacing={3}>
            {/* Operation Info */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {operation.name || 'Unnamed Operation'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                    ID: {operation.id}
                  </Typography>
                </Box>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarToday sx={{ fontSize: 18, color: 'action.active' }} />
                  <Box>
                    <Typography variant="body2">
                      {format(new Date(operation.timestamp), 'MMMM dd, yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(operation.timestamp), 'HH:mm:ss')}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Person sx={{ fontSize: 18, color: 'action.active' }} />
                  <Typography variant="body2">
                    {operation.userName || 'Unknown User'}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>

            {/* References & Notes */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                {operation.refNumber && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Receipt sx={{ fontSize: 18, color: 'action.active' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Reference Number
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {operation.refNumber}
                      </Typography>
                    </Box>
                  </Stack>
                )}

                {operation.referenceId && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Receipt sx={{ fontSize: 18, color: 'action.active' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        External Reference
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {operation.referenceId}
                      </Typography>
                    </Box>
                  </Stack>
                )}

                {operation.notes && (
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Note sx={{ fontSize: 18, color: 'action.active', mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {operation.notes}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Transfer Summary */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle1" fontWeight="medium">
              Transfer Summary:
            </Typography>
            <Chip
              label={`${operation.transfers.length} total transfers`}
              size="small"
              color="primary"
              variant="outlined"
            />
            {transfersIn.length > 0 && (
              <Chip
                label={`${transfersIn.length} incoming`}
                size="small"
                color="success"
                variant="outlined"
                icon={<MoveDown />}
              />
            )}
            {transfersOut.length > 0 && (
              <Chip
                label={`${transfersOut.length} outgoing`}
                size="small"
                color="warning"
                variant="outlined"
                icon={<MoveUp />}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Transfers Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Direction</TableCell>
              <TableCell>Item/Unit</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Destination</TableCell>
              <TableCell>Box/Container</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {operation.transfers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No transfers found in this operation
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              operation.transfers.map((transfer) => {
                const directionInfo = getDirectionInfo(transfer.direction);

                return (
                  <TableRow key={transfer.id}>
                    <TableCell>
                      <Chip
                        label={directionInfo.label}
                        size="small"
                        color={directionInfo.color}
                        icon={directionInfo.icon}
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {transfer.itemName || 'Unknown Item'}
                        </Typography>
                        {transfer.unitBarcode && (
                          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {transfer.unitBarcode}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transfer.quantity}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {transfer.sourceName ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Business sx={{ fontSize: 16, color: 'action.active' }} />
                          <Typography variant="body2">
                            {transfer.sourceName}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No source
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {transfer.destinationName ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Storage sx={{ fontSize: 16, color: 'action.active' }} />
                          <Typography variant="body2">
                            {transfer.destinationName}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No destination
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {transfer.boxRefNumber ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Inventory sx={{ fontSize: 16, color: 'action.active' }} />
                          <Typography variant="body2" fontFamily="monospace">
                            {transfer.boxRefNumber}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Independent
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OperationDetails;